import {
  RisksAndRewards,
  NoRisksWrapper,
  ListItem,
  RouterLink,
} from './styled';
import { SlideContent } from '../styled';
import { RiskRewardStatement } from '@/components/RiskReward';
import { Heading, Bones, Text } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import type { ReactNode } from 'react';

type StatementValue = 'Pass' | 'Fail' | 'No data' | 'UnInitialised';
type Severity = 'minor' | 'major' | 'none';

interface Statement {
  name: string;
  title: string;
  description: string;
  formattedDescription: string;
  context: Record<string, string>;
  value: StatementValue;
  question: string;
  severity: Severity;
}

const Rewards = ({ children }: { children: ReactNode }) => (
  <>
    <Heading as="h3" typography="xSmall" color="whiteRGBA" opacity={0.5}>
      Rewards
    </Heading>
    {children}
  </>
);

const Risks = ({ children }: { children: ReactNode }) => (
  <>
    <Heading as="h3" typography="xSmall" color="whiteRGBA" opacity={0.5}>
      Risks
    </Heading>
    {children}
  </>
);

interface Props {
  isFetching?: boolean;
  tickerSymbol?: string;
  riskKeys?: string[];
  rewardKeys?: string[];
  statements?: Record<string, Statement>;
  hasRisks?: boolean;
  hasRewards?: boolean;
  canonicalUrl?: string;
}

const RisksRewardsSlide = ({
  isFetching = true,
  tickerSymbol = '',
  canonicalUrl = '',
  riskKeys = [],
  rewardKeys = [],
  statements = {},
  hasRisks = false,
  hasRewards = false,
}: Props) => {
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'RisksRewardsSlide',
  });
  return (
    <SlideContent
      as="section"
      rowGap="16px"
      alignContent="space-between"
      data-cy-id="risks-and-rewards-slide"
    >
      <RisksAndRewards rowGap="16px" alignContent="start">
        {isFetching ? (
          <>
            <Rewards>
              <div>
                <Bones maxWidth="90%" marginTop="x0_5" marginBottom="x2" />
                <Bones maxWidth="75%" marginTop="x0_5" marginBottom="x2" />
                <Bones maxWidth="80%" marginTop="x0_5" marginBottom="x2" />
              </div>
            </Rewards>
            <Risks>
              <div>
                <Bones maxWidth="95%" marginTop="x0_5" marginBottom="x2" />
                <Bones maxWidth="85%" marginTop="x0_5" marginBottom="x2" />
              </div>
            </Risks>
          </>
        ) : (
          <>
            {hasRewards && (
              <Rewards>
                <ul>
                  {rewardKeys.map((n) => {
                    return (
                      <ListItem key={statements[n].name}>
                        <RiskRewardStatement
                          description={statements[n].formattedDescription}
                          typography="xSmall"
                        />
                      </ListItem>
                    );
                  })}
                </ul>
              </Rewards>
            )}
            <Risks>
              {hasRisks ? (
                <ul>
                  {riskKeys.map((n) => {
                    return (
                      <ListItem key={statements[n].name}>
                        <RiskRewardStatement
                          severity={statements[n].severity}
                          description={statements[n].formattedDescription}
                          typography="xSmall"
                        />
                      </ListItem>
                    );
                  })}
                </ul>
              ) : (
                <NoRisksWrapper>
                  <Text typography="xSmall" color="whiteRGBA" opacity={0.3}>
                    No risks detected for {tickerSymbol} from our risks checks.
                  </Text>
                </NoRisksWrapper>
              )}
            </Risks>
          </>
        )}
      </RisksAndRewards>
      {canonicalUrl.length > 0 && (
        <RouterLink
          styleType="loudLarge"
          display="block"
          data-cy-id="risks-and-rewards-cta"
          to={canonicalUrl}
          onClick={() => {
            trackEvent({
              type: 'track',
              modifier: 'View all Risks and Rewards',
              action: 'click',
            });
          }}
        >
          View all Risks and Rewards
        </RouterLink>
      )}
    </SlideContent>
  );
};

export default RisksRewardsSlide;
